import { type FC, useCallback, useState } from 'react'

import { type ChatMessageStatus } from '@api'
import { useUnit } from 'effector-react'
import { useQueryClient } from 'react-query'

import RefreshIcon from '@/assets/icons/icon-rotate-right.svg'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { $creatorId } from '@/models/creators.ts'
import { classNames } from '@/utils/helpers'

import { IconButton } from '../../../components/icon-button'
import { MESSAGE_STATUS_OPTIONS } from '../overview-table/constants'

const DEFAULT_OPTION = { text: 'All', value: 'all' }
const OPTIONS = [DEFAULT_OPTION, ...MESSAGE_STATUS_OPTIONS]

interface Props {
  onChange: (value?: ChatMessageStatus) => void
}

export const OverviewHeader: FC<Props> = ({ onChange }) => {
  const creatorId = useUnit($creatorId)
  const queryClient = useQueryClient()
  const [isRefreshing, setIsRefreshing] = useState(false)

  const handleChange = (value: string) => {
    onChange(
      value === DEFAULT_OPTION.value ? undefined : (value as ChatMessageStatus),
    )
  }

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true)
    await queryClient.invalidateQueries([creatorId, 'ppv-overview'])
    setIsRefreshing(false)
  }, [creatorId, queryClient])

  return (
    <div className="flex items-center justify-between">
      <div className="text-xl font-semibold">PPV Overview</div>
      <div className="flex items-center gap-4">
        <IconButton
          icon={RefreshIcon}
          onClick={handleRefresh}
          tooltip="Refresh"
          iconClassName={classNames('h-5 w-5', isRefreshing && 'animate-spin')}
          disabled={isRefreshing}
        />
        <Select
          onValueChange={handleChange}
          defaultValue={DEFAULT_OPTION.value}
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {OPTIONS.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                >
                  {option.text}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}
